import React, { useEffect, useState, useRef } from 'react'
import { Button } from '../../common/Button'
import { FormikObj } from './constant'
import { useFormik } from 'formik'
import SelectFiled from '../../common/textfield/SelectFiled'
import { failed } from "../../common/Toastify";
import { Col, Row } from 'react-bootstrap'
import TextInput from '../../common/textfield/TextInput'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { selectedRequestType } from '../Patient/Constants'
import { ArrowBack } from '@mui/icons-material'
import { usePatientEncounterHistoryQuery } from '../../../hooks/ReactQueryHooks/usePatientEncounterHistoryQuery'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { useGetPdfDocumentsQuery } from '../../../hooks/ReactQueryHooks/useGetPdfDocumentsQuery'
import PdfViewerComponent, { validatePdfBeforeNext } from '../../common/components/PdfViewerComponent'
import { getPatientData, getSingleUser } from '../../api/Individual'
import { splitContactNumber } from '../../../utils/phoneNumberSupport'
import ServiceLocationModal from './ServiceLocationModal'
import { safeAgeString } from '../../../utils/DateSupport'
import { capitalizeWords } from '../../../utils/capitalizeWords'
import { getPractitionerSignatureSource } from '../../../utils/signatureSupport'

const ServiceRequest = () => {
    const [openModal, setOpenModal] = useState(false)
    const [patientData, setPatientData] = useState(true)
    const [encounteredData, setEncounteredData] = useState([])
    const [searchParams] = useSearchParams();
    const [documentOptions, setDocumentOptions] = useState([]);
    const [allDocuments, setAllDocuments] = useState([]);
    const [currentRequestType, setCurrentRequestType] = useState(null);
    const [pdfFormValuesReady, setPdfFormValuesReady] = useState(false);
    const [nextLoading, setNextLoading] = useState(false);
    const instanceRef = useRef(null);
    const initialPdfFormValues = useRef(null);
    const practitionerId = useSelector((state) => state?.auth?.user?.["custom:unique_id"]);
    const paramsObj = Object.fromEntries(searchParams?.entries())
    const requestTypeId = localStorage.getItem("requestTypeId")
    const requestType = paramsObj?.requestType || selectedRequestType.find((obj) => obj?.id == requestTypeId)?.value || "Lab"
    const navigate = useNavigate()
    const user = useSelector((state) => state?.auth?.user);
    const adminOrgs = useSelector((state) => state?.auth?.user?.organizations) 

    const filterDocumentsByRequestType = (data, reqType) => {
        return reqType == 'Lab' ?
            data.filter(item => item?.category?.[0]?.coding?.[0]?.code === "Lab Requisition").map(item => ({ ...item, name: item?.documentName, value: item?.id }))
                : ( reqType == 'Diagnostic' ? 
                data.filter(item => item?.category?.[0]?.coding?.[0]?.code === "Diagnostic Requisition").map(item => ({ ...item, name: item?.documentName, value: item?.id }))
                : data.filter(item => item?.category?.[0]?.coding?.[0]?.code === "Referral").map(item => ({ ...item, name: item?.documentName, value: item?.id })))
    };

    const onSuccessOfDocumentsList = (data) => {
        setAllDocuments(data); // Store all documents
        const filteredDocs = filterDocumentsByRequestType(data, currentRequestType || requestType);
        setDocumentOptions(filteredDocs);
    };
    useGetPdfDocumentsQuery({ orgId:adminOrgs?.[0]?.id, onSuccess: onSuccessOfDocumentsList });

    const onSuccessForEncounterHistory = (data) => {
        setEncounteredData(data?.map((item) => ({ ...item, name: item?.id + (item?.period?.start ? `(${moment(item?.period?.start).format("MM-DD-YYYY")} | ${moment(item?.period?.start).format("hh:mm A")})` : ''), value: item?.id })));
    };

    usePatientEncounterHistoryQuery({ onSuccess: onSuccessForEncounterHistory, startDate: moment().subtract(3, 'months').format("YYYY-MM-DD"), endDate: moment().format("YYYY-MM-DD"), patientId: paramsObj?.patientId, practitionerId: user?.["custom:unique_id"], refetchOnWindowFocus: false });
    
    const getCategoryVar = (reqType) => {
        return reqType == 'Lab' ? 'Lab Requisition' : ( reqType == 'Diagnostic' ? 'Diagnostic Requisition' : 'Referral');
    };
    
    const categoryVar = getCategoryVar(currentRequestType || requestType);

    const formik = useFormik({
        ...FormikObj,
        initialValues: {
            ...FormikObj.initialValues,
            requestType: requestType
        },
        onSubmit: async () => {
            setNextLoading(true);
            try {
                const validation = await validatePdfBeforeNext(
                    instanceRef.current,
                    initialPdfFormValues.current
                );
                if (!validation.ok) {
                    failed(validation.message);
                    return;
                }
                setOpenModal(true);
            } finally {
                setNextLoading(false);
            }
        },
    });

    // Handle requestType change
    const handleRequestTypeChange = (e) => {
        const newRequestType = e.target.value;
        setCurrentRequestType(newRequestType);
        formik.setFieldValue('requestType', newRequestType);
        formik.setFieldValue('documentId', ''); // Clear document selection
        
        // Re-filter documents based on new request type
        if (allDocuments.length > 0) {
            const filteredDocs = filterDocumentsByRequestType(allDocuments, newRequestType);
            setDocumentOptions(filteredDocs);
        }
    };

    useEffect(() => {
        let fullPatientName = "";
        let fullPractitionerName = "";
        const tempFormValues = {};

        (async () => {
            try {
                const patientRes = await getPatientData(paramsObj.patientId);
                const patient = patientRes.result;
                setPatientData(patient);

                fullPatientName = capitalizeWords(patient.name?.[0]?.text);
                
                const patientDOB = patient.birthDate.replace(/-/g, "/");
                tempFormValues["patient-dob"] = moment(patient.birthDate).format("MMM-DD-YYYY").toUpperCase();
                tempFormValues["patient-name"] = fullPatientName;
                tempFormValues["patient-street"] = patient.address[0].line[0];
                tempFormValues["patient-city"] = patient.address[0].city;
                tempFormValues["patient-state"] = patient.address[0].state;
                tempFormValues["patient-postalcode"] = patient.address[0].postalCode;
                tempFormValues["patient-sex"] = capitalizeWords(patient.gender);
                tempFormValues["patient-HCN"] = patient.identifier.find(i => i.type === "HCN")?.value || "";
                tempFormValues["patient-telephone"] = patient.telecom.find(t => t.system === "phone")?.value;
                tempFormValues["encounter-id"] = paramsObj.encounterId || "";
                tempFormValues["date"] = moment().format("MMM-DD-YYYY");
                tempFormValues["patient-name-given"] = capitalizeWords(patient.name[0].given[0]);
                tempFormValues["patient-name-family"] = capitalizeWords(patient.name[0].family);
                tempFormValues["patient-age"] = safeAgeString(patientDOB);
                tempFormValues["patient-email"] = patient.telecom.find(t => t.system === "email")?.value;

                const pracRes = await getSingleUser({id: practitionerId, type: "Practitioner", userOrgId: adminOrgs[0].id });
                const prac = pracRes.data;

                fullPractitionerName = capitalizeWords(prac.name?.[0]?.text);

                initialPdfFormValues.current = {
                    ...tempFormValues,
                    "practitioner-name": fullPractitionerName,
                    "practitioner-license": prac.qualification[0].identifier[0].value || "",
                    "practitioner-telephone": splitContactNumber(prac.telecom.find(t => t.system === "phone")?.value || "").phone_number,
                    "practitioner-street": prac.address[0].line[0],
                    "practitioner-city": prac.address[0].city,
                    "practitioner-state": prac.address[0].state,
                    "practitioner-postalcode": prac.address[0].postalCode,
                    "practitioner-signature_af_image": getPractitionerSignatureSource(prac.signatureURL),
                    "practitioner-name-given": capitalizeWords(prac.name[0].given[0]),
                    "practitioner-name-family": capitalizeWords(prac.name[0].family),
                    "practitioner-fax": prac.telecom.find(t => t.system === "phone" && t.use === "work")?.value,
                    "practitioner-email": prac.telecom.find(t => t.system === "email")?.value
                };
                setPdfFormValuesReady(true);

                formik.setValues({
                    encounterId: paramsObj.encounterId || "",
                    documentId: formik.values.documentId,
                    requestType,
                    patientName: fullPatientName,
                    practitionerName: fullPractitionerName,
                });
            } catch (err) {
                failed(err.response?.data?.message || err.response?.data?.error || err.message);
            }
        })();
    }, []);

    const handleFormChange = (e) => {
        if (e?.target?.id === "encounterId") {
            initialPdfFormValues.current = {
                ...initialPdfFormValues.current,
                "encounter-id": e?.target?.value,
            }
            instanceRef?.current?.setFormFieldValues({
                "encounter-id": e?.target?.value,
            });
        }
    };

    useEffect(() => {
        localStorage.setItem("activeTableKey", "seventh");
    }, [])
    
    const encounterRow = encounteredData?.find(
        (row) => row.value === formik.values?.encounterId || row.id === formik.values?.encounterId
    );
    const selectedEncounterOrgId =
        encounterRow?.serviceProvider?.reference?.split("/")?.[1]
        || encounterRow?.managingOrganization?.reference?.split("/")?.[1]
        || null;


    return (
        <>
            {openModal ? <ServiceLocationModal
                onHide={() => setOpenModal(false)}
                modalShow={openModal}
                paramsObj={paramsObj}
                practitionerData={user}
                instanceRef={instanceRef}
                requestType={currentRequestType || requestType}
                categoryVar={categoryVar}
                patientData={patientData}
                orgName={adminOrgs?.[0]?.name}
                fromServiceRequest={true}
                encounterId={formik.values?.encounterId}
                documentName={documentOptions?.find(item=> item?.id === formik.values?.documentId)?.name}
                encounterOrgId={selectedEncounterOrgId}
            /> : null}
            <section className="common-listing">
                <div className="heading-wrap mb-3">
                    {paramsObj?.fromPatient === "true" && <span style={{ cursor: "pointer" }} onClick={() => navigate(-1)}><ArrowBack /></span>}
                </div>
                <div className="custom-card p-4">
                    <form className="common-form border-fields" onSubmit={formik.handleSubmit} onChange={handleFormChange}>
                        <Row>
                            <Col>
                                {!paramsObj?.encounterId
                                    ? <SelectFiled
                                        formik={formik}
                                        options={encounteredData}
                                        fieldtype={"TextInput"}
                                        keyField={"encounterId"}
                                        label={"Encounter ID"}
                                        placeholder={"111D1"}
                                    />
                                    : <TextInput
                                        disabled
                                        formik={formik}
                                        fieldtype={"TextInput"}
                                        keyField={"encounterId"}
                                        label={"Encounter ID"}
                                        placeholder={"111D1"}
                                    />}
                            </Col>
                            <Col>
                                <SelectFiled
                                    keyField={"requestType"}
                                    label={"Request Type"}
                                    formik={formik}
                                    options={selectedRequestType.map(item => ({ ...item, name: item.name, value: item.value }))}
                                    onChange={handleRequestTypeChange}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <SelectFiled
                                    keyField={"documentId"}
                                    label={"Select Document"}
                                    formik={formik}
                                    options={documentOptions
                                        .map(item => ({ ...item, name: item.documentName, value: item.id }))
                                        .sort((a, b) =>
                                        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
                                    )}
                                    readOnly={!formik.values?.encounterId}
                                />
                            </Col>
                        </Row>
                        <div className="Pdf-viewer">
                            {formik.values.documentId && pdfFormValuesReady ? (
                                <PdfViewerComponent
                                    key={formik.values.documentId}
                                    initialPdfFormValues={initialPdfFormValues.current}
                                    documentId={documentOptions.find(item => item?.id === formik.values.documentId).id}
                                    instanceRef={instanceRef}
                                />
                            ) : formik.values.documentId ? (
                                <p className="text-muted mb-0">Loading form data…</p>
                            ) : null}
                        </div>
                        <div className="btn-wrap" style={{ display: "flex" }}>
                            <Button onClick={() => paramsObj?.fromPatient === "true" ? navigate(-1) : window.close()} variant="secondary" title="Cancel"> Cancel </Button> 
                            <Button type="submit" isLoading={nextLoading} disabled={!pdfFormValuesReady || nextLoading}>
                                Next
                            </Button>
                        </div>
                    </form>
                </div>
            </section>
        </>
    )
}

export default ServiceRequest